/**
 * PATH: packages/document-editor/src/contracts.ts
 * FILE_ID: DOCEDITOR-CONTRACTS-001
 * PURPOSE: Versioned, JSON-first document and report-domain contracts.
 * WHY: Persisted documents must remain portable across editors and PDF renderers.
 * RELATED: serialization.ts, reportExtensions.ts
 */
import type { JSONContent } from '@tiptap/core'

export const DOCUMENT_SCHEMA_VERSION = 1 as const
export type DocumentSchemaVersion = typeof DOCUMENT_SCHEMA_VERSION

export interface PageMarginsMm {
  top: number
  right: number
  bottom: number
  left: number
}

export interface PageSettings {
  size: 'A4'
  orientation: 'portrait' | 'landscape'
  marginsMm: PageMarginsMm
}

export const DEFAULT_A4_PAGE_SETTINGS: PageSettings = {
  size: 'A4',
  orientation: 'portrait',
  marginsMm: { top: 20, right: 20, bottom: 20, left: 20 },
}

export interface DocumentMetadata {
  title?: string
  pageSettings: PageSettings
  createdAt?: string
  updatedAt?: string
  domain?: string
}

export interface VersionedDocument {
  schemaVersion: DocumentSchemaVersion
  kind: 'quotech.document'
  content: JSONContent
  metadata: DocumentMetadata
}

export interface FileRecordReference {
  fileId?: string
  url?: string
  name?: string
  mimeType?: string
}

export interface GeneratedFactMetadata {
  factId: string
  source: string
  generatedAt: string
  status: 'generated' | 'verified' | 'stale'
}

export interface AuditedOverrideMetadata {
  factId: string
  reason: string
  overriddenBy: string
  overriddenAt: string
  originalValueHash: string
  originalValue: string
  currentValue: string
}

export interface MergeFieldDefinition {
  key: string
  label: string
  format?: string
  fallback?: string
}

export interface DocumentValidationOptions {
  domainNodeNames?: readonly string[]
  allowDataImageUrls?: boolean
}

export interface DocumentValidationResult {
  valid: boolean
  errors: string[]
}

const CORE_NODES = new Set([
  'doc', 'text', 'paragraph', 'heading', 'bulletList', 'orderedList', 'listItem',
  'blockquote', 'horizontalRule', 'hardBreak', 'image', 'table', 'tableRow',
  'tableHeader', 'tableCell', 'pageBreak', 'sectionBreak', 'pageSettings',
  'headerBlock', 'footerBlock', 'generatedFactBlock', 'auditedOverride',
  'mergeField',
])
const SAFE_URL = /^(https?:\/\/|\/(?!\/)|\.{1,2}\/|#)/i
const SAFE_DATA_IMAGE = /^data:image\/(?:png|jpe?g|gif|webp);base64,/i
const SAFE_KEY = /^[a-zA-Z][a-zA-Z0-9_.-]{0,127}$/

export function createDocument(
  content: JSONContent = { type: 'doc', content: [{ type: 'paragraph' }] },
  metadata: Partial<DocumentMetadata> = {},
): VersionedDocument {
  return {
    schemaVersion: DOCUMENT_SCHEMA_VERSION,
    kind: 'quotech.document',
    content,
    metadata: {
      ...metadata,
      pageSettings: metadata.pageSettings ?? DEFAULT_A4_PAGE_SETTINGS,
    },
  }
}

export function isSafeDocumentUrl(value: unknown, allowDataImages = false): value is string {
  return typeof value === 'string'
    && (SAFE_URL.test(value) || (allowDataImages && SAFE_DATA_IMAGE.test(value)))
}

function isIsoDate(value: unknown): value is string {
  return typeof value === 'string' && !Number.isNaN(Date.parse(value))
}

function validateReportNode(node: JSONContent, path: string, errors: string[]) {
  const attrs = node.attrs ?? {}
  if (node.type === 'generatedFactBlock') {
    if (!SAFE_KEY.test(String(attrs.factId ?? ''))) errors.push(`${path}.attrs.factId is invalid`)
    if (!attrs.source) errors.push(`${path}.attrs.source is required`)
    if (!isIsoDate(attrs.generatedAt)) errors.push(`${path}.attrs.generatedAt must be an ISO date`)
    if (!['generated', 'verified', 'stale'].includes(String(attrs.status))) {
      errors.push(`${path}.attrs.status is invalid`)
    }
  }
  if (node.type === 'auditedOverride') {
    for (const field of ['factId', 'reason', 'overriddenBy', 'originalValueHash']) {
      if (!attrs[field]) errors.push(`${path}.attrs.${field} is required`)
    }
    for (const field of ['originalValue', 'currentValue']) {
      if (typeof attrs[field] !== 'string') errors.push(`${path}.attrs.${field} must be a string`)
    }
    if (!SAFE_KEY.test(String(attrs.factId ?? ''))) errors.push(`${path}.attrs.factId is invalid`)
    if (!isIsoDate(attrs.overriddenAt)) errors.push(`${path}.attrs.overriddenAt must be an ISO date`)
  }
  if (node.type === 'mergeField' && !SAFE_KEY.test(String(attrs.key ?? ''))) {
    errors.push(`${path}.attrs.key is invalid`)
  }
  if (node.type === 'sectionBreak' && !['nextPage', 'continuous'].includes(String(attrs.kind))) {
    errors.push(`${path}.attrs.kind is invalid`)
  }
  if (node.type === 'pageSettings') {
    if (attrs.size !== 'A4') errors.push(`${path}.attrs.size must be A4`)
    if (!['portrait', 'landscape'].includes(String(attrs.orientation))) {
      errors.push(`${path}.attrs.orientation is invalid`)
    }
    for (const field of ['marginTopMm', 'marginRightMm', 'marginBottomMm', 'marginLeftMm']) {
      const margin = Number(attrs[field])
      if (!Number.isFinite(margin) || margin < 0 || margin > 60) {
        errors.push(`${path}.attrs.${field} must be between 0 and 60`)
      }
    }
  }
  if ((node.type === 'headerBlock' || node.type === 'footerBlock')
    && !['document', 'section', 'firstPage'].includes(String(attrs.scope))) {
    errors.push(`${path}.attrs.scope is invalid`)
  }
}

function validateNode(
  node: JSONContent,
  path: string,
  allowedNodes: Set<string>,
  options: DocumentValidationOptions,
  errors: string[],
) {
  if (!node || typeof node !== 'object' || !node.type) {
    errors.push(`${path} must be a typed JSON node`)
    return
  }
  if (!allowedNodes.has(node.type)) errors.push(`${path}.type "${node.type}" is not allowed`)
  if (node.type === 'image') {
    const { fileId, src, url } = node.attrs ?? {}
    if (fileId && !SAFE_KEY.test(String(fileId))) errors.push(`${path}.attrs.fileId is invalid`)
    if (!fileId && !isSafeDocumentUrl(src ?? url, options.allowDataImageUrls)) {
      errors.push(`${path} image requires a fileId or safe URL`)
    }
  }
  validateReportNode(node, path, errors)
  node.content?.forEach((child, index) => {
    validateNode(child, `${path}.content[${index}]`, allowedNodes, options, errors)
  })
}

export function validateDocument(
  input: unknown,
  options: DocumentValidationOptions = {},
): DocumentValidationResult {
  const errors: string[] = []
  if (!input || typeof input !== 'object') return { valid: false, errors: ['document must be an object'] }
  const document = input as Partial<VersionedDocument>
  if (document.schemaVersion !== DOCUMENT_SCHEMA_VERSION) errors.push('unsupported schemaVersion')
  if (document.kind !== 'quotech.document') errors.push('kind must be quotech.document')
  if (document.metadata?.pageSettings?.size !== 'A4') errors.push('pageSettings.size must be A4')
  if (!['portrait', 'landscape'].includes(String(document.metadata?.pageSettings?.orientation))) {
    errors.push('pageSettings.orientation is invalid')
  }
  const margins = document.metadata?.pageSettings?.marginsMm
  for (const field of ['top', 'right', 'bottom', 'left'] as const) {
    const value = margins?.[field]
    if (!Number.isFinite(Number(value)) || Number(value) < 0 || Number(value) > 60) {
      errors.push(`pageSettings.marginsMm.${field} must be between 0 and 60`)
    }
  }
  if (document.content?.type !== 'doc') errors.push('content root must be doc')
  if (document.content) {
    const allowedNodes = new Set([...CORE_NODES, ...(options.domainNodeNames ?? [])])
    validateNode(document.content, 'content', allowedNodes, options, errors)
  }
  return { valid: errors.length === 0, errors }
}
